import { isMobile } from '@lemonade-hq/cdk';
import type { SchemaData } from '@lemonade-hq/chat-engine';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import type { InterpolableTextProps, UniclientAction } from '@lemonade-hq/uniclient-chat';
import { isNativeApp, isNativeIOS } from '@lemonade-hq/uniclient-mobile-bridge';
import {
  Button,
  Flex,
  getPixelValue,
  Layout,
  LinkButton,
  spacing,
  Text,
  useMobileKeyboardHeight,
} from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { COMPONENT_ID } from '../adapters';
import { WIZARD_ACTIONS_ELEMENT_ID } from '../utils/consts';
import { MOBILE_BOTTOM_SAFE_AREA } from './layout';
import * as styles from './WizardActions.css';
import { DISCLAIMER_ANIMATION_DELAY, WizardDisclaimer } from './WizardDisclaimer';
import { ExplanationBubble } from './WizardExplanationBubble';
import { FOOTER_HEIGHT } from './WizardFooter.css';
import { frameScrollOpacityVar } from './WizardMain.css';

const EXPLANATION_TEXT_ANIMATION_DURATION: Milliseconds = 0.3;

export const BOTTOM_PADDING = isNativeApp()
  ? `${MOBILE_BOTTOM_SAFE_AREA + getPixelValue(spacing.s04)}`
  : getPixelValue(spacing.s24);

interface WizardActionsProps {
  readonly hidden?: boolean;
  readonly primaryAction?: UniclientAction;
  readonly secondaryAction?: UniclientAction;
  readonly disabled: boolean;
  readonly loading: boolean;
  readonly onAction: (action: UniclientAction) => Promise<unknown>;
  readonly shouldPadTop?: boolean;
  readonly explanationContent?: InterpolableTextProps;
  readonly hasFooter?: boolean;
  readonly hideBackground?: boolean;
  readonly answers: SchemaData;
  readonly animationDelay?: number;
  readonly currentStepIndex: number;
  readonly isSticky: boolean;
  readonly disclaimer?: InterpolableTextProps;
}

export const WizardActions: React.FC<WizardActionsProps> = ({
  hidden = false,
  primaryAction,
  secondaryAction,
  disabled = false,
  loading,
  onAction,
  shouldPadTop,
  explanationContent,
  hasFooter,
  hideBackground,
  answers,
  animationDelay = 0,
  currentStepIndex,
  isSticky = false,
  disclaimer,
}) => {
  const isMobileDevice = useMemo(() => isMobile(), []);
  const [isNativeKeyboardOpen, setIsNativeKeyboardOpen] = useState(false);

  const onMobileKeyboardHeightChange = useCallback((isOpen: boolean) => {
    setIsNativeKeyboardOpen(isOpen);
  }, []);

  useMobileKeyboardHeight({ onChange: onMobileKeyboardHeightChange });
  const isPrimaryActionDisabled = (disabled && !primaryAction?.enabledEvenIfInvalid) || primaryAction?.disabled;
  const isSecondaryActionDisabled =
    secondaryAction != null &&
    (((disabled && !secondaryAction.enabledEvenIfInvalid) || secondaryAction.disabled) ?? loading);
  const lastDelayedStepIndex = useRef(-1);

  const performPrimaryAction = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (primaryAction != null && !isPrimaryActionDisabled) void onAction(primaryAction);
    },
    [isPrimaryActionDisabled, onAction, primaryAction],
  );

  const performSecondaryAction = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (secondaryAction != null && !isSecondaryActionDisabled) {
        void onAction(secondaryAction);
      }
    },
    [isSecondaryActionDisabled, onAction, secondaryAction],
  );

  const isMobileKeyboardOpen = isMobileDevice && isNativeKeyboardOpen;

  if (isMobileKeyboardOpen && !isNativeIOS()) {
    return null;
  }

  return (
    <AnimatePresence mode="popLayout">
      {!hidden && (
        <Flex
          alignItems={{ sm: 'left', lg: 'start' }}
          animate={{
            opacity: frameScrollOpacityVar,
            transition: {
              delay: currentStepIndex === lastDelayedStepIndex.current ? 0 : animationDelay / 1000,
              duration: 0,
            },
          }}
          as={motion.div}
          className={styles.container({ visible: !(isMobileKeyboardOpen && isNativeIOS()) })}
          exit={{ opacity: 0 }}
          flexDirection="column"
          gap={{ sm: spacing.s16, lg: spacing.s24 }}
          id={WIZARD_ACTIONS_ELEMENT_ID}
          initial={{
            opacity: 0,
          }}
          justifyContent={{
            sm: 'flex-end',
            lg: 'flex-start',
          }}
          marginBlockStart={shouldPadTop ? spacing.s40 : 0}
          onAnimationComplete={() => (lastDelayedStepIndex.current = currentStepIndex)}
          pb={{
            sm: hasFooter ? FOOTER_HEIGHT : `${BOTTOM_PADDING}px`,
          }}
          width={{
            sm: '100%',
            lg: 'auto',
          }}
        >
          <LayoutGroup>
            <Layout
              as={motion.div}
              className={!hideBackground ? styles.actionsOverlayContainer({ isSticky, loading }) : undefined}
              layout="position"
              position="absolute"
            />

            <AnimatePresence mode="popLayout">
              {explanationContent != null && !loading && (
                <motion.div
                  animate={{ height: 'auto', opacity: 1, transition: { height: { delay: -1 } } }}
                  exit={{ height: 0, opacity: 0 }}
                  initial={{ height: 0, opacity: 0 }}
                  layout="position"
                  transition={{ duration: EXPLANATION_TEXT_ANIMATION_DURATION }}
                >
                  <ExplanationBubble answers={answers} onAction={onAction} text={explanationContent} />
                </motion.div>
              )}
            </AnimatePresence>

            <Flex flexDirection="column" gap={spacing.s24}>
              {primaryAction != null && !primaryAction.hidden && (
                <Button
                  data-animated
                  disabled={isPrimaryActionDisabled}
                  form={COMPONENT_ID}
                  loading={loading}
                  onClick={performPrimaryAction}
                  type="submit"
                  variant={primaryAction.primary ? 'primary' : 'secondary'}
                >
                  {primaryAction.content}
                </Button>
              )}
              {secondaryAction != null && !secondaryAction.hidden && (
                <LinkButton
                  className={styles.secondaryAction}
                  data-animated
                  disabled={isSecondaryActionDisabled}
                  layout="position"
                  onClick={performSecondaryAction}
                >
                  <Text type="bodyMedium">{secondaryAction.content}</Text>
                </LinkButton>
              )}
            </Flex>

            <AnimatePresence>
              {disclaimer != null && (
                <WizardDisclaimer
                  animationDelay={animationDelay + DISCLAIMER_ANIMATION_DELAY}
                  answers={answers}
                  layout="position"
                  onAction={onAction}
                  text={disclaimer}
                />
              )}
            </AnimatePresence>
          </LayoutGroup>
        </Flex>
      )}
    </AnimatePresence>
  );
};
